import { BACKSPACE } from '@angular/cdk/keycodes';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Host,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewEncapsulation
} from '@angular/core';
import { fromEvent, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MwcNoAnimationDirective } from 'mwc/core/no-animation';
import { MwcSafeAny } from 'mwc/core/types';

import { MwcSelectSearchComponent } from './select-search.component';
import { MwcSelectItemInterface, MwcSelectModeType, MwcSelectTopControlItemType } from './select.types';

@Component({
  selector: 'mwc-select-top-control',
  exportAs: 'mwcSelectTopControl',
  preserveWhitespaces: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  template: `
    <!--single mode-->
    <ng-container [ngSwitch]="mode">
      <ng-container *ngSwitchCase="'default'">
        <!-- <mwc-select-search
          [mwcId]="mwcId"
          [disabled]="disabled"
          [value]="inputValue!"
          [showInput]="showSearch"
          [mirrorSync]="false"
          [autofocus]="autofocus"
          [focusTrigger]="open"
          (isComposingChange)="isComposingChange($event)"
          (valueChange)="onInputValueChange($event)"
        ></mwc-select-search> -->
        <mwc-select-item
          *ngIf="isShowSingleLabel"
          [deletable]="false"
          [disabled]="false"
          [removeIcon]="removeIcon"
          [label]="listOfTopItem[0].mwcLabel"
          [contentTemplateOutlet]="customTemplate"
          [contentTemplateOutletContext]="listOfTopItem[0]"
        ></mwc-select-item>
      </ng-container>
      <ng-container *ngSwitchDefault>
        <!--multiple or tags mode-->
        <mwc-select-item
          *ngFor="let item of listOfSlicedItem; trackBy: trackValue"
          [removeIcon]="removeIcon"
          [label]="item.mwcLabel"
          [disabled]="item.mwcDisabled || disabled"
          [contentTemplateOutlet]="item.contentTemplateOutlet"
          [deletable]="true"
          [contentTemplateOutletContext]="item.contentTemplateOutletContext"
          (delete)="onDeleteItem(item.contentTemplateOutletContext)"
        ></mwc-select-item>
        <!-- <mwc-select-search
          [mwcId]="mwcId"
          [disabled]="disabled"
          [value]="inputValue!"
          [autofocus]="autofocus"
          [showInput]="true"
          [mirrorSync]="true"
          [focusTrigger]="open"
          (isComposingChange)="isComposingChange($event)"
          (valueChange)="onInputValueChange($event)"
        ></mwc-select-search> -->
      </ng-container>
    </ng-container>
    <mwc-select-placeholder *ngIf="isShowPlaceholder" [placeholder]="placeHolder"></mwc-select-placeholder>
  `,
  host: { class: 'mwc-select-selector' }
})
export class MwcSelectTopControlComponent implements OnChanges, OnInit, OnDestroy {
  @Input() mwcId: string | null = null;
  @Input() showSearch = false;
  @Input() placeHolder: string | TemplateRef<MwcSafeAny> | null = null;
  @Input() open = false;
  @Input() maxTagCount: number = Infinity;
  @Input() autofocus = false;
  @Input() disabled = false;
  @Input() mode: MwcSelectModeType = 'default';
  @Input() customTemplate: TemplateRef<{ $implicit: MwcSelectItemInterface }> | null = null;
  @Input() maxTagPlaceholder: TemplateRef<{ $implicit: MwcSafeAny[] }> | null = null;
  @Input() removeIcon: TemplateRef<MwcSafeAny> | null = null;
  @Input() listOfTopItem: MwcSelectItemInterface[] = [];
  @Input() tokenSeparators: string[] = [];
  @Input() mwcSelectSearchComponent!: MwcSelectSearchComponent;
  @Output() readonly tokenize = new EventEmitter<string[]>();
  @Output() readonly inputValueChange = new EventEmitter<string>();
  @Output() readonly deleteItem = new EventEmitter<MwcSelectItemInterface>();

  listOfSlicedItem: MwcSelectTopControlItemType[] = [];
  isShowPlaceholder = true;
  isShowSingleLabel = false;
  isComposing = false;
  inputValue: string | null = null;

  private destroy$ = new Subject<void>();

  updateTemplateVariable(): void {
    const isSelectedValueEmpty = this.listOfTopItem.length === 0;
    this.isShowPlaceholder = isSelectedValueEmpty && !this.isComposing && !this.inputValue;
    this.isShowSingleLabel = !isSelectedValueEmpty && !this.isComposing && !this.inputValue;
  }

  isComposingChange(isComposing: boolean): void {
    this.isComposing = isComposing;
    this.updateTemplateVariable();
  }

  onInputValueChange(value: string): void {
    if (value !== this.inputValue) {
      this.inputValue = value;
      this.updateTemplateVariable();
      this.inputValueChange.emit(value);
      this.tokenSeparate(value, this.tokenSeparators);
    }
  }

  tokenSeparate(inputValue: string, tokenSeparators: string[]): void {
    const includesSeparators = (str: string | string[], separators: string[]): boolean => {
      // eslint-disable-next-line @typescript-eslint/prefer-for-of
      for (let i = 0; i < separators.length; ++i) {
        if (str.lastIndexOf(separators[i]) > 0) {
          return true;
        }
      }
      return false;
    };
    const splitBySeparators = (str: string | string[], separators: string[]): string[] => {
      const reg = new RegExp(`[${separators.join()}]`);
      const array = (str as string).split(reg).filter(token => token);
      return [...new Set(array)];
    };
    if (
      inputValue &&
      inputValue.length &&
      tokenSeparators.length &&
      this.mode !== 'default' &&
      includesSeparators(inputValue, tokenSeparators)
    ) {
      const listOfLabel = splitBySeparators(inputValue, tokenSeparators);
      this.tokenize.next(listOfLabel);
    }
  }

  clearInputValue(): void {
    if (this.mwcSelectSearchComponent) {
      this.mwcSelectSearchComponent.clearInputValue();
    }
  }

  focus(): void {
    if (this.mwcSelectSearchComponent) {
      this.mwcSelectSearchComponent.focus();
    }
  }

  blur(): void {
    if (this.mwcSelectSearchComponent) {
      this.mwcSelectSearchComponent.blur();
    }
  }

  trackValue(_index: number, option: MwcSelectTopControlItemType): MwcSafeAny {
    return option.mwcValue;
  }

  onDeleteItem(item: MwcSelectItemInterface): void {
    if (!this.disabled && !item.mwcDisabled) {
      this.deleteItem.next(item);
    }
  }

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private ngZone: NgZone,
    @Host() @Optional() public noAnimation: MwcNoAnimationDirective | null
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    const { listOfTopItem, maxTagCount, customTemplate, maxTagPlaceholder } = changes;
    if (listOfTopItem) {
      this.updateTemplateVariable(); 
    }
    if (listOfTopItem || maxTagCount || customTemplate || maxTagPlaceholder) {
      const listOfSlicedItem: MwcSelectTopControlItemType[] = this.listOfTopItem.slice(0, this.maxTagCount).map(o => ({
        mwcLabel: o.mwcLabel,
        mwcValue: o.mwcValue,
        mwcDisabled: o.mwcDisabled,
        contentTemplateOutlet: this.customTemplate,
        contentTemplateOutletContext: o
      }));
      if (this.listOfTopItem.length > this.maxTagCount) {
        const exceededLabel = `+ ${this.listOfTopItem.length - this.maxTagCount} ...`;
        const listOfSelectedValue = this.listOfTopItem.map(item => item.mwcValue);
        const exceededItem = {
          mwcLabel: exceededLabel,
          mwcValue: '$$__mwc_exceeded_item',
          mwcDisabled: true,
          contentTemplateOutlet: this.maxTagPlaceholder,
          contentTemplateOutletContext: listOfSelectedValue.slice(this.maxTagCount)
        };
        listOfSlicedItem.push(exceededItem);
      }
      this.listOfSlicedItem = listOfSlicedItem;
    }
  }

  ngOnInit(): void {
    this.ngZone.runOutsideAngular(() => {
      fromEvent<MouseEvent>(this.elementRef.nativeElement, 'click')
        .pipe(takeUntil(this.destroy$))
        .subscribe(event => {
          // `HTMLElement.focus()` is a native DOM API which doesn't require Angular to run change detection.
          setTimeout(() => {
            if (event.target !== this.mwcSelectSearchComponent?.inputElement?.nativeElement) {
              this.mwcSelectSearchComponent?.focus();
            }
          }, 0);
        });

      fromEvent<KeyboardEvent>(this.elementRef.nativeElement, 'keydown')
        .pipe(takeUntil(this.destroy$))
        .subscribe(event => {
          if (event.target instanceof HTMLInputElement) {
            const inputValue = event.target.value;

            if (
              event.keyCode === BACKSPACE &&
              this.mode !== 'default' &&
              !inputValue &&
              this.listOfTopItem.length > 0
            ) {
              event.preventDefault();
              // Run change detection only if the user has pressed the `Backspace` key and the following condition is met.
              this.ngZone.run(() => this.onDeleteItem(this.listOfTopItem[this.listOfTopItem.length - 1]));
            }
          }
        });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }
}
